import './MembershipCustomerSummaryCard.css';
import { Tag, ShieldCheck, Calendar, Hourglass } from 'lucide-react';
import type { MembershipDetail } from '../api/membership.api';

type MembershipCustomerSummaryCardProps = {
  membership?: MembershipDetail;
};

const formatDate = (value?: string): string => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-IN', { dateStyle: 'medium' });
};

const getStatusClass = (status: string): string => {
  const normalized = String(status || '').toUpperCase();
  if (normalized === 'ACTIVE') return 'is-active';
  if (normalized === 'EXPIRED') return 'is-expired';
  if (normalized === 'REVOKED') return 'is-revoked';
  return '';
};

const getDaysRemaining = (expiresAt?: string): { days: number; percent: number } | null => {
  if (!expiresAt) return null;
  const expiry = new Date(expiresAt);
  if (Number.isNaN(expiry.getTime())) return null;
  const now = Date.now();
  const diffMs = expiry.getTime() - now;
  const days = Math.max(Math.ceil(diffMs / 86400000), 0);
  const percent = Math.min(Math.max(days / 365, 0), 1) * 100;
  return { days, percent };
};

const getVisitProgress = (membership?: MembershipDetail): { used: number; total: number; remaining: number; percent: number } | null => {
  if (String(membership?.benefitType || '').toUpperCase() !== 'VISITS') return null;
  const total = Number(membership?.includedVisits ?? 0);
  if (!Number.isFinite(total) || total <= 0) return null;
  const used = Math.min(Math.max(Number(membership?.visitsUsed ?? 0), 0), total);
  const remaining = Math.max(Number(membership?.visitsRemaining ?? total - used), 0);
  const percent = Math.min(Math.max((remaining / total) * 100, 0), 100);
  return { used, total, remaining, percent };
};

const MembershipCustomerSummaryCard = ({ membership }: MembershipCustomerSummaryCardProps) => {
  const remaining = getDaysRemaining(membership?.expiresAt);
  const visitProgress = getVisitProgress(membership);

  return (
    <article className="appointments-v2-card membership-customer-summary">
      <div className="appointments-v2-card-header"><h3>Customer membership</h3></div>
      <div className="appointments-v2-card-body membership-customer-summary__body">
        <div className="membership-customer-summary__grid">
          <div className="membership-customer-summary__field">
            <span className="membership-customer-summary__icon" aria-hidden="true"><Tag size={16} /></span>
            <div className="membership-customer-summary__field-content">
              <p className="membership-customer-summary__label">Membership</p>
              <p className="membership-customer-summary__value">{membership?.nameSnapshot || '—'}</p>
            </div>
          </div>
          <div className="membership-customer-summary__field">
            <span className="membership-customer-summary__icon" aria-hidden="true"><ShieldCheck size={16} /></span>
            <div className="membership-customer-summary__field-content">
              <p className="membership-customer-summary__label">Status</p>
              <span className={`membership-customer-summary__status ${getStatusClass(membership?.status || '')}`}>
                {membership?.status || '—'}
              </span>
            </div>
          </div>
          <div className="membership-customer-summary__field">
            <span className="membership-customer-summary__icon" aria-hidden="true"><Calendar size={16} /></span>
            <div className="membership-customer-summary__field-content">
              <p className="membership-customer-summary__label">Issued</p>
              <p className="membership-customer-summary__value">{formatDate(membership?.issuedAt)}</p>
            </div>
          </div>
          <div className="membership-customer-summary__field">
            <span className="membership-customer-summary__icon" aria-hidden="true"><Hourglass size={16} /></span>
            <div className="membership-customer-summary__field-content">
              <p className="membership-customer-summary__label">Expires</p>
              <p className="membership-customer-summary__value">{formatDate(membership?.expiresAt)}</p>
            </div>
          </div>
        </div>

        {visitProgress ? (
          <div className="membership-customer-summary__countdown">
            <div className="membership-customer-summary__countdown-header">
              <p className="membership-customer-summary__label">Visits remaining</p>
              <strong className="membership-customer-summary__countdown-value">{visitProgress.remaining}</strong>
            </div>
            <div className="membership-customer-summary__progress-track">
              <div
                className="membership-customer-summary__progress-fill"
                style={{ width: `${visitProgress.percent}%` }}
              />
            </div>
            <p className="membership-customer-summary__meta">
              {visitProgress.used} used of {visitProgress.total}
            </p>
          </div>
        ) : remaining ? (
          <div className="membership-customer-summary__countdown">
            <div className="membership-customer-summary__countdown-header">
              <p className="membership-customer-summary__label">Days remaining</p>
              <strong className="membership-customer-summary__countdown-value">{remaining.days}</strong>
            </div>
            <div className="membership-customer-summary__progress-track">
              <div
                className="membership-customer-summary__progress-fill"
                style={{ width: `${remaining.percent}%` }}
              />
            </div>
          </div>
        ) : null}
      </div>
    </article>
  );
};

export default MembershipCustomerSummaryCard;
